import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const PULL_THRESHOLD = 72;
const MAX_PULL = 100;
const DAMPEN = 0.45;

function getScrollTop(): number {
  return Math.max(
    window.scrollY || 0,
    document.documentElement.scrollTop || 0,
    document.body.scrollTop || 0
  );
}

export default function PullToRefresh(): React.JSX.Element | null {
  const [pullY, setPullY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const pullYRef = useRef(0);
  const isRefreshingRef = useRef(false);

  const setPull = (v: number) => {
    pullYRef.current = v;
    setPullY(v);
  };

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return;
      // Selalu catat posisi awal sentuhan
      startYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startYRef.current === null || isRefreshingRef.current) return;
      
      const y = e.touches[0].clientY;
      const delta = y - startYRef.current;

      // Jika kita berada di puncak halaman (atau sangat dekat dengan puncak)
      // dan user menggeser ke bawah (delta > 0)
      if (getScrollTop() <= 10 && delta > 0) {
        pullingRef.current = true;
        
        // Block default behavior (seperti bounce di iOS atau reload native di Android)
        if (e.cancelable) {
          e.preventDefault();
        }
        
        setPull(Math.min(delta * DAMPEN, MAX_PULL));
      } else {
        // Jika sedang tidak menarik ke bawah dari puncak, reset
        if (pullingRef.current) {
          pullingRef.current = false;
          setPull(0);
        }
        // Update startY jika user scroll ke atas (supaya saat sampai puncak dan langsung tarik, delta dihitung dari puncak)
        if (getScrollTop() > 10) {
          startYRef.current = y;
        }
      }
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) {
        startYRef.current = null;
        return;
      }

      pullingRef.current = false;
      startYRef.current = null;

      if (pullYRef.current >= PULL_THRESHOLD) {
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        setPull(PULL_THRESHOLD);
        setTimeout(() => window.location.reload(), 500);
      } else {
        setPull(0);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    // passive: false penting agar preventDefault() berjalan
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  if (pullY === 0 && !isRefreshing) return null;

  const progress = Math.min(pullY / PULL_THRESHOLD, 1);
  const triggered = isRefreshing || pullY >= PULL_THRESHOLD;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] flex items-end justify-center pointer-events-none pb-2"
      style={{
        height: `${pullY}px`,
        transition: isRefreshing || (!pullingRef.current && pullY === 0) ? 'height 0.25s ease' : 'none',
      }}
    >
      <div
        className="flex items-center justify-center rounded-full shadow-lg"
        style={{
          width: 38,
          height: 38,
          background: 'var(--gradient-primary)',
          opacity: progress,
          transform: `scale(${0.5 + 0.5 * progress})`,
          transition: isRefreshing ? 'transform 0.25s ease' : 'none',
        }}
      >
        <RefreshCw
          className="h-4 w-4 text-white"
          style={{
            transform: `rotate(${triggered ? 360 : progress * 200}deg)`,
            transition: triggered ? 'transform 0.3s ease' : 'none',
            animation: isRefreshing ? 'spin 0.7s linear infinite' : 'none',
          }}
        />
      </div>
    </div>
  );
}
